import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, formatApiError, BACKEND_URL, openApiFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, ShoppingCart, FileText, PackageCheck, BarChart3, Pencil, Loader2, Eye, Download } from "lucide-react";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import ExportButton from "@/components/ExportButton";

export default function Purchases() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTab = location.state?.tab || "solicitudes";
  const [tab, setTab] = useState(initialTab);

  // Si llegamos desde el formulario con state.tab, sincronizar y limpiar state.
  useEffect(() => {
    if (location.state?.tab && location.state.tab !== tab) {
      setTab(location.state.tab);
    }
    // No limpiamos location.state aquí porque FacturasCompra también lo lee (newFactura).
    // Lo limpia FacturasCompra una vez consumido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.tab]);

  return (
    <div className="space-y-6" data-testid="purchases-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Compras</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Módulo de Compras
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">Solicitudes, órdenes, recepciones, facturas y comparación de costos.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="solicitudes" data-testid="tab-solicitudes"><FileText size={14} className="mr-2" />Solicitudes</TabsTrigger>
          <TabsTrigger value="ordenes" data-testid="tab-ordenes"><ShoppingCart size={14} className="mr-2" />Órdenes</TabsTrigger>
          <TabsTrigger value="recepciones" data-testid="tab-recepciones"><PackageCheck size={14} className="mr-2" />Recepciones</TabsTrigger>
          <TabsTrigger value="facturas" data-testid="tab-facturas-compra">Facturas compra</TabsTrigger>
          <TabsTrigger value="comparacion" data-testid="tab-comparacion"><BarChart3 size={14} className="mr-2" />Comparar costos</TabsTrigger>
        </TabsList>
        <TabsContent value="solicitudes"><Solicitudes /></TabsContent>
        <TabsContent value="ordenes"><Ordenes /></TabsContent>
        <TabsContent value="recepciones"><Recepciones /></TabsContent>
        <TabsContent value="facturas"><FacturasCompra navigate={navigate} location={location} /></TabsContent>
        <TabsContent value="comparacion"><ComparacionCostos /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===================== SOLICITUDES =====================
function Solicitudes() {
  const [items, setItems] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [prods, setProds] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [viewSol, setViewSol] = useState(null); // solicitud abierta en modo Ver
  function empty() { return { bodega_id: "", fecha: new Date().toISOString().slice(0,10), motivo: "", lineas: [] }; }

  const load = async () => {
    try {
      const [s, b, p] = await Promise.all([
        api.get("/purchases/solicitudes"),
        api.get("/masters/catalog/bodegas"),
        api.get("/masters/productos-comerciales"),
      ]);
      setItems(s.data); setBodegas(b.data); setProds(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const addLinea = () => setForm(f => ({...f, lineas: [...f.lineas, { producto_comercial_id: "", producto_nombre: "", cantidad: 1, observaciones: "" }]}));
  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas];
    nl[i] = {...nl[i], [field]: val};
    if (field === "producto_comercial_id") {
      const p = prods.find(x => x.id === val);
      if (p) nl[i].producto_nombre = p.nombre_comercial;
    }
    return {...f, lineas: nl};
  });

  const save = async () => {
    try {
      if (editingId) {
        await api.patch(`/purchases/solicitudes/${editingId}`, form);
        toast.success("Solicitud actualizada");
      } else {
        await api.post("/purchases/solicitudes", form);
        toast.success("Solicitud creada");
      }
      setDialogOpen(false); setForm(empty()); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => { if (!window.confirm(`¿Eliminar ${it.numero}?`)) return; await api.delete(`/purchases/solicitudes/${it.id}`); load(); };
  const editar = (it) => {
    setEditingId(it.id);
    setForm({
      bodega_id: it.bodega_id || "",
      fecha: it.fecha || new Date().toISOString().slice(0,10),
      motivo: it.motivo || "",
      lineas: (it.lineas || []).map(l => ({ ...l })),
    });
    setDialogOpen(true);
  };
  const descargarPDF = async (it) => {
    try {
      const res = await api.get(`/purchases/solicitudes/${it.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${it.numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const verPDF = (it) => {
    // Abre el PDF inline en una nueva pestaña usando el token
    const token = localStorage.getItem("ps_access_token") || "";
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    fetch(`${API_BASE}/purchases/solicitudes/${it.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), "_blank"))
      .catch(e => toast.error(formatApiError(e)));
  };

  const cols = [
    { label: "Número", accessor: "numero" },
    { label: "Fecha", accessor: "fecha" },
    { label: "Solicitante", accessor: "solicitante_nombre" },
    { label: "Estado", accessor: "estado" },
    { label: "# Líneas", accessor: (r) => r.lineas?.length || 0 },
  ];

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end gap-2">
        <ExportButton title="Solicitudes de compra" columns={cols} rows={items} filename="solicitudes-compra" />
        <Button onClick={() => { setForm(empty()); setEditingId(null); setDialogOpen(true); }} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="create-solicitud-btn">
          <Plus size={16} className="mr-2" /> Nueva solicitud
        </Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Solicitante</th><th>Bodega</th><th>Estado</th><th># Ítems</th><th className="w-40 text-right">Acciones</th></tr></thead>
          <tbody>
            {items.map(it => {
              const bloqueada = it.estado === "procesada";
              return (
              <tr key={it.id} data-testid={`sol-row-${it.id}`}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td>{it.solicitante_nombre}</td>
                <td>{bodegas.find(b => b.id === it.bodega_id)?.nombre || "—"}</td>
                <td><Badge variant="outline" className="capitalize">{it.estado}</Badge></td>
                <td>{it.lineas?.length || 0}</td>
                <td>
                  <div className="flex gap-0.5 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => setViewSol(it)} title="Ver detalle" data-testid={`view-sol-${it.id}`}><Eye size={14} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => verPDF(it)} title="Ver PDF" data-testid={`pdf-sol-${it.id}`}><FileText size={14} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => descargarPDF(it)} title="Descargar PDF" data-testid={`download-sol-${it.id}`}><Download size={14} className="text-[#205c45]" /></Button>
                    {!bloqueada && (
                      <Button size="icon" variant="ghost" onClick={() => editar(it)} title="Editar" data-testid={`edit-sol-${it.id}`}><Pencil size={14} className="text-[#205c45]" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Eliminar"><Trash2 size={14} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin solicitudes</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Modal Ver detalle */}
      <Dialog open={!!viewSol} onOpenChange={(o) => !o && setViewSol(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-sol-dialog">
          <DialogHeader><DialogTitle>Solicitud {viewSol?.numero}</DialogTitle></DialogHeader>
          {viewSol && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3 rounded-md border border-[#d1dbd6] p-3 bg-[#f7f9f8]">
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha</span><div>{viewSol.fecha}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Solicitante</span><div>{viewSol.solicitante_nombre || "—"}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Bodega</span><div>{bodegas.find(b => b.id === viewSol.bodega_id)?.nombre || "—"}</div></div>
                <div className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Motivo</span><div>{viewSol.motivo || "—"}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Estado</span><div><Badge variant="outline" className="capitalize">{viewSol.estado}</Badge></div></div>
              </div>
              <table className="erp-table">
                <thead><tr><th className="w-10">#</th><th>Producto</th><th className="w-24 text-right">Cantidad</th><th>Observaciones</th></tr></thead>
                <tbody>
                  {(viewSol.lineas || []).map((l, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="font-medium">{l.producto_nombre || "—"}</td>
                      <td className="text-right tabular-nums">{l.cantidad}</td>
                      <td className="text-xs">{l.observaciones || "—"}</td>
                    </tr>
                  ))}
                  {(!viewSol.lineas || viewSol.lineas.length === 0) && <tr><td colSpan={4} className="text-center py-4 text-[#8ba397]">Sin líneas</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => descargarPDF(viewSol)} disabled={!viewSol}><Download size={14} className="mr-1" />Descargar PDF</Button>
            <Button onClick={() => setViewSol(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? `Editar solicitud` : "Nueva solicitud de compra"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Bodega destino</Label>
              <Select value={form.bodega_id} onValueChange={(v) => setForm({...form, bodega_id: v})}>
                <SelectTrigger data-testid="sol-bodega-select"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
            <div><Label>Motivo</Label><Input value={form.motivo} onChange={(e) => setForm({...form, motivo: e.target.value})} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos</div>
              <Button size="sm" variant="outline" onClick={addLinea}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="w-24">Cantidad</th><th>Observaciones</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <Select value={l.producto_comercial_id} onValueChange={(v) => updateLinea(i, "producto_comercial_id", v)}>
                        <SelectTrigger className="h-9"><SelectValue placeholder="..." /></SelectTrigger>
                        <SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td><Input className="h-9" type="number" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseInt(e.target.value)||0)} /></td>
                    <td><Input className="h-9" value={l.observaciones} onChange={(e) => updateLinea(i, "observaciones", e.target.value)} /></td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({...f, lineas: f.lineas.filter((_, x) => x !== i)}))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
                {form.lineas.length === 0 && <tr><td colSpan={4} className="text-center py-4 text-[#8ba397] text-sm">Sin productos</td></tr>}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-solicitud-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== ORDENES =====================
function Ordenes() {
  const [items, setItems] = useState([]);
  const [provs, setProvs] = useState([]);
  const [prods, setProds] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [viewOrd, setViewOrd] = useState(null);
  function empty() { return { proveedor_id: "", fecha: new Date().toISOString().slice(0,10), fecha_entrega_esperada: "", forma_pago_id: "", observaciones: "", lineas: [] }; }

  const load = async () => {
    const [o, p, pr, fp] = await Promise.all([
      api.get("/purchases/ordenes"),
      api.get("/masters/proveedores"),
      api.get("/masters/productos-comerciales"),
      api.get("/masters/catalog/formas_pago"),
    ]);
    setItems(o.data); setProvs(p.data); setProds(pr.data); setFormasPago(fp.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const addLinea = () => setForm(f => ({...f, lineas: [...f.lineas, { producto_comercial_id: "", producto_nombre: "", cantidad: 1, costo_unitario: 0, subtotal: 0 }]}));
  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas];
    nl[i] = {...nl[i], [field]: val};
    if (field === "producto_comercial_id") {
      const p = prods.find(x => x.id === val);
      if (p) { nl[i].producto_nombre = p.nombre_comercial; nl[i].costo_unitario = p.precio_costo || 0; }
    }
    nl[i].subtotal = (parseFloat(nl[i].cantidad)||0) * (parseFloat(nl[i].costo_unitario)||0);
    return {...f, lineas: nl};
  });
  const total = useMemo(() => form.lineas.reduce((s,l) => s + (l.subtotal||0), 0), [form.lineas]);

  const save = async () => {
    try {
      const payload = {...form};
      if (!payload.forma_pago_id) payload.forma_pago_id = null;
      if (editingId) {
        await api.patch(`/purchases/ordenes/${editingId}`, payload);
        toast.success("Orden actualizada");
      } else {
        await api.post("/purchases/ordenes", payload);
        toast.success("Orden creada");
      }
      setDialogOpen(false); setForm(empty()); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (it) => { if (!window.confirm(`¿Eliminar ${it.numero}?`)) return; await api.delete(`/purchases/ordenes/${it.id}`); load(); };
  const editar = (it) => {
    setEditingId(it.id);
    setForm({
      proveedor_id: it.proveedor_id || "",
      fecha: it.fecha || new Date().toISOString().slice(0,10),
      fecha_entrega_esperada: it.fecha_entrega_esperada || "",
      forma_pago_id: it.forma_pago_id || "",
      observaciones: it.observaciones || "",
      lineas: (it.lineas || []).map(l => ({ ...l })),
    });
    setDialogOpen(true);
  };
  const descargarPDF = async (it) => {
    try {
      const res = await api.get(`/purchases/ordenes/${it.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${it.numero}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const verPDF = (it) => {
    const token = localStorage.getItem("ps_access_token") || "";
    const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    fetch(`${API_BASE}/purchases/ordenes/${it.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => window.open(URL.createObjectURL(b), "_blank"))
      .catch(e => toast.error(formatApiError(e)));
  };

  const cols = [
    { label: "Número", accessor: "numero" },
    { label: "Fecha", accessor: "fecha" },
    { label: "Proveedor", accessor: "proveedor_nombre" },
    { label: "Total", accessor: (r) => `$${r.total?.toFixed(2)}` },
    { label: "Estado", accessor: "estado" },
  ];

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end gap-2">
        <ExportButton title="Órdenes de compra" columns={cols} rows={items} filename="ordenes-compra" />
        <Button onClick={() => { setForm(empty()); setEditingId(null); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-orden-btn"><Plus size={16} className="mr-2" />Nueva orden</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Proveedor</th><th>Entrega esperada</th><th className="text-right">Total</th><th>Estado</th><th className="w-40 text-right">Acciones</th></tr></thead>
          <tbody>
            {items.map(it => {
              const bloqueada = ["recibida_total", "recibida_parcial", "anulada"].includes(it.estado);
              return (
              <tr key={it.id} data-testid={`ord-row-${it.id}`}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="font-medium">{it.proveedor_nombre}</td>
                <td>{it.fecha_entrega_esperada || "—"}</td>
                <td className="text-right font-semibold tabular-nums">${it.total?.toFixed(2)}</td>
                <td><Badge variant="outline" className="capitalize">{it.estado}</Badge></td>
                <td>
                  <div className="flex gap-0.5 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => setViewOrd(it)} title="Ver detalle" data-testid={`view-ord-${it.id}`}><Eye size={14} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => verPDF(it)} title="Ver PDF" data-testid={`pdf-ord-${it.id}`}><FileText size={14} className="text-[#205c45]" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => descargarPDF(it)} title="Descargar PDF" data-testid={`download-ord-${it.id}`}><Download size={14} className="text-[#205c45]" /></Button>
                    {!bloqueada && (
                      <Button size="icon" variant="ghost" onClick={() => editar(it)} title="Editar" data-testid={`edit-ord-${it.id}`}><Pencil size={14} className="text-[#205c45]" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => remove(it)} title="Eliminar"><Trash2 size={14} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin órdenes</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Modal Ver detalle */}
      <Dialog open={!!viewOrd} onOpenChange={(o) => !o && setViewOrd(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="view-ord-dialog">
          <DialogHeader><DialogTitle>Orden de Compra {viewOrd?.numero}</DialogTitle></DialogHeader>
          {viewOrd && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-3 rounded-md border border-[#d1dbd6] p-3 bg-[#f7f9f8]">
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Fecha</span><div>{viewOrd.fecha}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Proveedor</span><div className="font-medium">{viewOrd.proveedor_nombre || "—"}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Estado</span><div><Badge variant="outline" className="capitalize">{viewOrd.estado}</Badge></div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Entrega esperada</span><div>{viewOrd.fecha_entrega_esperada || "—"}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Forma de pago</span><div>{formasPago.find(f => f.id === viewOrd.forma_pago_id)?.nombre || "—"}</div></div>
                <div><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Total</span><div className="text-lg font-bold text-[#205c45]">${viewOrd.total?.toFixed(2)}</div></div>
                {viewOrd.observaciones && (<div className="col-span-3"><span className="text-[10px] uppercase tracking-wider text-[#5c7368]">Observaciones</span><div>{viewOrd.observaciones}</div></div>)}
              </div>
              <table className="erp-table">
                <thead><tr><th className="w-10">#</th><th>Producto</th><th className="w-20 text-right">Cant.</th><th className="w-28 text-right">Costo U.</th><th className="w-28 text-right">Subtotal</th></tr></thead>
                <tbody>
                  {(viewOrd.lineas || []).map((l, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="font-medium">{l.producto_nombre || "—"}</td>
                      <td className="text-right tabular-nums">{l.cantidad}</td>
                      <td className="text-right tabular-nums">${l.costo_unitario?.toFixed(2)}</td>
                      <td className="text-right tabular-nums font-semibold">${l.subtotal?.toFixed(2)}</td>
                    </tr>
                  ))}
                  {(!viewOrd.lineas || viewOrd.lineas.length === 0) && <tr><td colSpan={5} className="text-center py-4 text-[#8ba397]">Sin líneas</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => descargarPDF(viewOrd)} disabled={!viewOrd}><Download size={14} className="mr-1" />Descargar PDF</Button>
            <Button onClick={() => setViewOrd(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar orden" : "Nueva orden de compra"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Proveedor</Label>
              <ProveedorCombobox
                value={form.proveedor_id}
                onChange={(v) => setForm({...form, proveedor_id: v})}
                proveedores={provs}
                placeholder="Buscar proveedor por razón social, RUC…"
                testId="orden-prov"
              />
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
            <div><Label>Entrega esperada</Label><Input type="date" value={form.fecha_entrega_esperada} onChange={(e) => setForm({...form, fecha_entrega_esperada: e.target.value})} /></div>
            <div>
              <Label>Forma pago</Label>
              <Select value={form.forma_pago_id || ""} onValueChange={(v) => setForm({...form, forma_pago_id: v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{formasPago.map(fp => <SelectItem key={fp.id} value={fp.id}>{fp.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-3"><Label>Observaciones</Label><Input value={form.observaciones} onChange={(e) => setForm({...form, observaciones: e.target.value})} /></div>
          </div>
          <div className="border border-[#d1dbd6] rounded-md mt-4">
            <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
              <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Productos</div>
              <Button size="sm" variant="outline" onClick={addLinea}><Plus size={14} className="mr-1" />Agregar</Button>
            </div>
            <table className="erp-table">
              <thead><tr><th>Producto</th><th className="w-20">Cant.</th><th className="w-24">Costo U.</th><th className="w-28 text-right">Subtotal</th><th className="w-10"></th></tr></thead>
              <tbody>
                {form.lineas.map((l, i) => (
                  <tr key={i}>
                    <td><Select value={l.producto_comercial_id} onValueChange={(v) => updateLinea(i, "producto_comercial_id", v)}><SelectTrigger className="h-9"><SelectValue placeholder="..." /></SelectTrigger><SelectContent>{prods.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre_comercial}</SelectItem>)}</SelectContent></Select></td>
                    <td><Input className="h-9" type="number" value={l.cantidad} onChange={(e) => updateLinea(i, "cantidad", parseInt(e.target.value)||0)} /></td>
                    <td><Input className="h-9" type="number" step="0.01" value={l.costo_unitario} onChange={(e) => updateLinea(i, "costo_unitario", parseFloat(e.target.value)||0)} /></td>
                    <td className="text-right font-semibold tabular-nums">${(l.subtotal||0).toFixed(2)}</td>
                    <td><Button size="icon" variant="ghost" onClick={() => setForm(f => ({...f, lineas: f.lineas.filter((_, x) => x !== i)}))}><Trash2 size={14} /></Button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-[#f7f9f8] border-t"><tr><td colSpan={3} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total</td><td colSpan={2} className="text-right font-bold text-lg tabular-nums px-3 py-2">${total.toFixed(2)}</td></tr></tfoot>
            </table>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-orden-btn">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== RECEPCIONES =====================
function Recepciones() {
  const [items, setItems] = useState([]);
  const [ordenes, setOrdenes] = useState([]);
  const [bodegas, setBodegas] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(empty());
  function empty() { return { orden_compra_id: "", fecha: new Date().toISOString().slice(0,10), resultado: "conforme", observaciones: "", lineas: [] }; }

  const load = async () => {
    const [r, o, b] = await Promise.all([
      api.get("/purchases/recepciones"),
      api.get("/purchases/ordenes"),
      api.get("/masters/catalog/bodegas"),
    ]);
    setItems(r.data);
    setOrdenes(o.data.filter(x => x.estado !== "recibida_total" && x.estado !== "anulada"));
    setBodegas(b.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const onOrdenChange = (id) => {
    const orden = ordenes.find(o => o.id === id);
    const lineas = (orden?.lineas || []).map(l => ({
      producto_comercial_id: l.producto_comercial_id,
      producto_nombre: l.producto_nombre,
      cantidad_recibida: l.cantidad,
      numero_lote: "",
      fecha_vencimiento: "",
      fecha_elaboracion: "",
      costo_unitario: l.costo_unitario || 0,
      bodega_id: bodegas[0]?.id || "",
    }));
    setForm({...form, orden_compra_id: id, lineas});
  };
  const updateLinea = (i, field, val) => setForm(f => {
    const nl = [...f.lineas]; nl[i] = {...nl[i], [field]: val}; return {...f, lineas: nl};
  });

  const save = async () => {
    try { await api.post("/purchases/recepciones", form); toast.success("Recepción registrada - lotes creados"); setDialogOpen(false); setForm(empty()); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-recepcion-btn"><Plus size={16} className="mr-2" />Nueva recepción</Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>OC</th><th>Proveedor</th><th>Resultado</th><th># Ítems</th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="font-mono text-xs">{ordenes.find(o => o.id === it.orden_compra_id)?.numero || it.orden_compra_id?.slice(0,8)}</td>
                <td>{it.proveedor_nombre}</td>
                <td><Badge className="fefo-safe border capitalize">{it.resultado}</Badge></td>
                <td>{it.lineas?.length || 0}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8ba397]">Sin recepciones</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva recepción</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Orden de compra</Label>
              <Select value={form.orden_compra_id} onValueChange={onOrdenChange}>
                <SelectTrigger data-testid="recep-orden-select"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{ordenes.map(o => <SelectItem key={o.id} value={o.id}>{o.numero} - {o.proveedor_nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
            <div>
              <Label>Resultado</Label>
              <Select value={form.resultado} onValueChange={(v) => setForm({...form, resultado: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conforme">Conforme</SelectItem>
                  <SelectItem value="parcial">Parcial</SelectItem>
                  <SelectItem value="con_observaciones">Con observaciones</SelectItem>
                  <SelectItem value="rechazada">Rechazada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Observaciones</Label><Input value={form.observaciones} onChange={(e) => setForm({...form, observaciones: e.target.value})} /></div>
          </div>
          {form.lineas.length > 0 && (
            <div className="border border-[#d1dbd6] rounded-md mt-4">
              <div className="px-3 py-2 bg-[#f7f9f8] border-b text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
                Ingrese datos del lote recibido (número de lote, vencimiento, bodega)
              </div>
              <table className="erp-table">
                <thead><tr><th>Producto</th><th className="w-20">Recibida</th><th>Nº Lote</th><th>F. Elab.</th><th>F. Venc.</th><th>Bodega</th><th className="w-24">Costo U.</th></tr></thead>
                <tbody>
                  {form.lineas.map((l, i) => (
                    <tr key={i}>
                      <td className="font-medium">{l.producto_nombre}</td>
                      <td><Input className="h-9" type="number" value={l.cantidad_recibida} onChange={(e) => updateLinea(i, "cantidad_recibida", parseInt(e.target.value)||0)} /></td>
                      <td><Input className="h-9" value={l.numero_lote} onChange={(e) => updateLinea(i, "numero_lote", e.target.value)} data-testid={`lote-num-${i}`} /></td>
                      <td><Input className="h-9" type="date" value={l.fecha_elaboracion} onChange={(e) => updateLinea(i, "fecha_elaboracion", e.target.value)} /></td>
                      <td><Input className="h-9" type="date" value={l.fecha_vencimiento} onChange={(e) => updateLinea(i, "fecha_vencimiento", e.target.value)} /></td>
                      <td><Select value={l.bodega_id} onValueChange={(v) => updateLinea(i, "bodega_id", v)}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger><SelectContent>{bodegas.map(b => <SelectItem key={b.id} value={b.id}>{b.nombre}</SelectItem>)}</SelectContent></Select></td>
                      <td><Input className="h-9" type="number" step="0.01" value={l.costo_unitario} onChange={(e) => updateLinea(i, "costo_unitario", parseFloat(e.target.value)||0)} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]" data-testid="save-recepcion-btn">Confirmar recepción</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== FACTURAS COMPRA =====================
function FacturasCompra({ navigate, location }) {
  const [items, setItems] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [dialog, setDialog] = useState(null); // { factura, sugerencias }
  const [enviarEmail, setEnviarEmail] = useState(false);
  const [retentionPrompt, setRetentionPrompt] = useState(null);
  const [editFc, setEditFc] = useState(null);     // { id, ...campos seguros editables }
  const [viewFc, setViewFc] = useState(null);     // factura para "ver detalle" (read-only)
  const [deleteFc, setDeleteFc] = useState(null); // factura a anular
  const [busyAction, setBusyAction] = useState(false);

  const load = async () => {
    try {
      const [f, t] = await Promise.all([
        api.get("/purchases/facturas"),
        api.get("/tipos-retencion"),
      ]);
      setItems(f.data);
      setTipos(t.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Si volvemos desde /compras/nueva con la factura creada, abrir el prompt de retención.
  useEffect(() => {
    if (location?.state?.newFactura) {
      setRetentionPrompt(location.state.newFactura);
      // Limpiamos el state para no re-disparar el prompt en renders posteriores.
      navigate(location.pathname, { replace: true, state: { tab: "facturas" } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.newFactura]);

  const abrirGenerar = async (fc) => {
    try {
      const { data } = await api.get(`/purchases/facturas/${fc.id}/sugerir-retencion`);
      const lines = (data.sugerencias || []).map(s => ({
        tipo: s.tipo, codigo_sri: s.codigo_sri, descripcion: s.descripcion,
        base_imponible: s.base, porcentaje: s.porcentaje, valor_retenido: s.valor,
      }));
      setDialog({ factura: fc, lineas: lines, fecha: new Date().toISOString().slice(0, 10) });
      setEnviarEmail(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateLine = (i, field, val) => setDialog(d => {
    const nl = [...d.lineas];
    nl[i] = { ...nl[i], [field]: val };
    if (field === "base_imponible" || field === "porcentaje") {
      const base = parseFloat(nl[i].base_imponible) || 0;
      const pct = parseFloat(nl[i].porcentaje) || 0;
      nl[i].valor_retenido = Math.round(base * pct) / 100;
    }
    if (field === "codigo_sri") {
      const t = tipos.find(x => x.codigo_sri === val);
      if (t) {
        nl[i].tipo = t.tipo;
        nl[i].descripcion = t.descripcion;
        nl[i].porcentaje = t.porcentaje;
        const base = parseFloat(nl[i].base_imponible) || 0;
        nl[i].valor_retenido = Math.round(base * t.porcentaje) / 100;
      }
    }
    return { ...d, lineas: nl };
  });

  const addLine = () => setDialog(d => ({ ...d, lineas: [...d.lineas, { tipo: "renta", codigo_sri: "", descripcion: "", base_imponible: 0, porcentaje: 0, valor_retenido: 0 }] }));
  const removeLine = (i) => setDialog(d => ({ ...d, lineas: d.lineas.filter((_, x) => x !== i) }));

  const totalRet = (dialog?.lineas || []).reduce((s, l) => s + (parseFloat(l.valor_retenido) || 0), 0);

  const [generando, setGenerando] = useState(false);

  const generar = async () => {
    if (!dialog?.lineas?.length) return toast.error("Agregue al menos un impuesto a retener");
    if (generando) return;
    setGenerando(true);
    const toastId = toast.loading("Generando retención y enviando al SRI… puede tardar 20–40 segundos. NO refresque ni vuelva a presionar.", { duration: Infinity });
    try {
      const payload = {
        fecha: dialog.fecha || new Date().toISOString().slice(0, 10),
        impuestos: dialog.lineas.map(l => ({
          tipo: l.tipo, codigo_sri: l.codigo_sri, descripcion: l.descripcion,
          base_imponible: parseFloat(l.base_imponible) || 0,
          porcentaje: parseFloat(l.porcentaje) || 0,
          valor_retenido: parseFloat(l.valor_retenido) || 0,
        })),
        enviar_email: enviarEmail,
        auto_firmar_enviar: true,
      };
      const { data } = await api.post(`/purchases/facturas/${dialog.factura.id}/generar-retencion`, payload);
      const estado = data.estado_sri || "borrador";
      toast.success(`✅ Retención ${data.numero} generada (${estado.toUpperCase()}) · Total $${data.total_retenido?.toFixed(2)}`, { id: toastId, duration: 6000 });
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e), { id: toastId, duration: 8000 });
    } finally {
      setGenerando(false);
    }
  };

  const verRide = (fc) => {
    if (!fc.retencion_id) return;
    openApiFile(`/invoicing/retenciones/${fc.retencion_id}/ride`, {
      filename: `RIDE-RET-${fc.retencion_numero || fc.retencion_id.slice(0,8)}.pdf`,
    }).catch((e) => toast.error(formatApiError(e)));
  };

  const abrirEdit = (fc) => setEditFc({
    id: fc.id,
    numero_factura: fc.numero_factura || "",
    autorizacion_sri: fc.autorizacion_sri || "",
    fecha_factura: fc.fecha_factura || "",
    dias_credito: fc.dias_credito ?? 0,
    fecha_vencimiento: fc.fecha_vencimiento || "",
    observaciones: fc.observaciones || "",
    _retencion_numero: fc.retencion_numero || null,
  });

  const guardarEdit = async () => {
    if (!editFc) return;
    try {
      setBusyAction(true);
      const payload = {
        numero_factura: editFc.numero_factura,
        autorizacion_sri: editFc.autorizacion_sri || null,
        fecha_factura: editFc.fecha_factura,
        dias_credito: editFc.dias_credito != null && editFc.dias_credito !== "" ? parseInt(editFc.dias_credito) : null,
        observaciones: editFc.observaciones,
      };
      await api.patch(`/purchases/facturas/${editFc.id}`, payload);
      toast.success("Factura actualizada");
      setEditFc(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAction(false); }
  };

  const eliminarFactura = async () => {
    if (!deleteFc) return;
    try {
      setBusyAction(true);
      const { data } = await api.delete(`/purchases/facturas/${deleteFc.id}`);
      const partes = [];
      if (data.retencion_anulada) partes.push(`retención ${data.retencion_anulada} anulada`);
      if (data.lotes_eliminados) partes.push(`${data.lotes_eliminados} lote(s) eliminados`);
      if (data.cxp_eliminada) partes.push("CxP eliminada");
      toast.success(
        `Factura ${data.factura_eliminada} eliminada${partes.length ? " · " + partes.join(" · ") : ""}`,
        { duration: 8000 }
      );
      setDeleteFc(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyAction(false); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center gap-2">
        <div className="text-xs text-[#5c7368] italic">Las facturas de compra generan automáticamente una CxP. La retención SRI se firma, envía y autoriza automáticamente si el certificado .p12 está configurado.</div>
        <Button onClick={() => navigate("/compras/nueva")} className="bg-[#205c45]" data-testid="create-factura-compra-btn">
          <Plus size={16} className="mr-2" /> Nueva factura compra
        </Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Nº Factura</th><th>Proveedor</th><th>Fecha</th><th>Vencimiento</th><th className="text-right">Total</th><th>Estado</th><th>Retención</th><th className="w-72"></th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="font-mono text-xs">
                  {it.numero_factura}
                  {it.origen === "sri_recibidos_import" && (
                    <div className="mt-0.5">
                      <Badge className="bg-[#eef2f9] text-[#2d4a73] border border-[#b2c5db] text-[9px] font-semibold" title="Importada automáticamente desde el SRI (comprobante autorizado)">
                        📥 Importada SRI
                      </Badge>
                    </div>
                  )}
                </td>
                <td className="font-medium">{it.proveedor_nombre}</td>
                <td>{it.fecha_factura}</td>
                <td>{it.fecha_vencimiento || "—"}</td>
                <td className="text-right font-semibold tabular-nums">${it.total?.toFixed(2)}</td>
                <td><Badge variant="outline" className="capitalize">{it.estado_contable}</Badge></td>
                <td>{it.retencion_numero ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-mono text-[10px] font-semibold text-[#205c45]">{it.retencion_numero}</span>
                    <span className="tabular-nums text-xs">${it.retencion_total?.toFixed(2)}</span>
                  </div>
                ) : <span className="text-xs text-[#8ba397]">—</span>}</td>
                <td>
                  <div className="flex gap-1 justify-end flex-wrap">
                    {it.retencion_id ? (
                      <Button size="sm" variant="outline" onClick={() => verRide(it)} data-testid={`ride-retencion-${it.id}`}>
                        <FileText size={14} className="mr-1" />RIDE
                      </Button>
                    ) : (
                      <Button size="sm" className="bg-[#205c45]" onClick={() => abrirGenerar(it)} data-testid={`gen-retencion-${it.id}`}>
                        Generar retención SRI
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => setViewFc(it)} title="Ver detalle de productos / pagos" data-testid={`view-fc-${it.id}`}>
                      <Eye size={14} className="text-[#205c45]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => abrirEdit(it)} title="Editar campos seguros (no afecta stock)" data-testid={`edit-fc-${it.id}`}>
                      <Pencil size={14} className="text-[#205c45]" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setDeleteFc(it)} title="Eliminar factura (con reversa)" data-testid={`delete-fc-${it.id}`}>
                      <Trash2 size={14} className="text-[#b53a25]" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin facturas de compra registradas aún</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-6xl w-[96vw] max-h-[92vh] overflow-y-auto" data-testid="ret-dialog">
          <DialogHeader>
            <DialogTitle>Generar retención SRI · Factura {dialog?.factura?.numero_factura}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <>
              <div className="rounded-md bg-[#e9f2ee] border border-[#b2d0c2] p-3 text-sm">
                <div className="grid grid-cols-3 gap-4">
                  <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Proveedor</span><div className="font-semibold">{dialog.factura.proveedor_nombre}</div></div>
                  <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">Subtotal</span><div className="tabular-nums">${dialog.factura.subtotal?.toFixed(2)}</div></div>
                  <div><span className="text-[#5c7368] text-xs uppercase tracking-wider">IVA</span><div className="tabular-nums">${dialog.factura.iva?.toFixed(2)}</div></div>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-[#5c7368] mt-2">
                <span className="italic">
                  Si el % del SRI cambió (ej. tabla vigente desde 1-marzo-2026), actualízalo en
                </span>
                <Link to="/admin/tipos-retencion" target="_blank" className="font-semibold text-[#205c45] underline hover:text-[#1a4a37]">
                  Admin · Catálogo retenciones SRI →
                </Link>
              </div>

              <div className="border border-[#d1dbd6] rounded-md mt-2">
                <div className="flex items-center justify-between px-3 py-2 bg-[#f7f9f8] border-b">
                  <div className="text-xs uppercase tracking-wider font-semibold text-[#5c7368]">Impuestos a retener</div>
                  <Button size="sm" onClick={addLine} className="bg-[#205c45] hover:bg-[#1a4a37]"><Plus size={14} className="mr-1" />Agregar</Button>
                </div>

                <div className="p-3 space-y-3">
                  {dialog.lineas.length === 0 && (
                    <div className="rounded-lg border-2 border-dashed border-[#d1dbd6] py-6 text-center text-sm text-[#8ba397]">
                      Sin impuestos. Toque "Agregar" para añadir uno.
                    </div>
                  )}
                  {dialog.lineas.map((l, i) => (
                    <div key={i} className="rounded-lg border border-[#d1dbd6] bg-white p-3 space-y-3 shadow-sm" data-testid={`ret-line-card-${i}`}>
                      {/* Fila 1: Tipo + Código SRI + delete */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                        <div className="md:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Tipo</Label>
                          <Select value={l.tipo} onValueChange={(v) => updateLine(i, "tipo", v)}>
                            <SelectTrigger className="h-10" data-testid={`ret-tipo-${i}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="renta">IR (Renta)</SelectItem>
                              <SelectItem value="iva">IVA</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-9">
                          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Código SRI</Label>
                          <Select value={l.codigo_sri} onValueChange={(v) => updateLine(i, "codigo_sri", v)}>
                            <SelectTrigger className="h-10" data-testid={`ret-codigo-${i}`}>
                              <SelectValue placeholder="Seleccione un código…" />
                            </SelectTrigger>
                            <SelectContent>
                              {tipos.filter(t => t.tipo === l.tipo).map(t => (
                                <SelectItem key={t.id} value={t.codigo_sri}>
                                  <span className="font-mono font-semibold mr-2">{t.codigo_sri}</span>
                                  · {t.porcentaje}% · {t.descripcion}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-1 flex justify-end">
                          <Button size="icon" variant="ghost" onClick={() => removeLine(i)} className="text-[#b53a25]" data-testid={`ret-del-${i}`}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>

                      {/* Fila 2: Descripción (full width) */}
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Descripción (editable)</Label>
                        <Input
                          className="h-10"
                          value={l.descripcion}
                          onChange={(e) => updateLine(i, "descripcion", e.target.value)}
                          data-testid={`ret-desc-${i}`}
                        />
                      </div>

                      {/* Fila 3: Base + % + Retenido */}
                      <div className="grid grid-cols-3 gap-3 pt-2 border-t border-[#d1dbd6]">
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Base imponible ($)</Label>
                          <Input
                            className="h-11 text-right tabular-nums text-base"
                            type="number"
                            step="0.01"
                            value={l.base_imponible}
                            onChange={(e) => updateLine(i, "base_imponible", parseFloat(e.target.value) || 0)}
                            data-testid={`ret-base-${i}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">% Retención</Label>
                          <Input
                            className="h-11 text-right tabular-nums text-base"
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={l.porcentaje}
                            onChange={(e) => updateLine(i, "porcentaje", parseFloat(e.target.value) || 0)}
                            placeholder="2.00"
                            data-testid={`ret-pct-${i}`}
                          />
                          <p className="text-[10px] text-[#5c7368] italic mt-1">Edite si el SRI publicó un nuevo %.</p>
                        </div>
                        <div>
                          <Label className="text-[10px] uppercase tracking-wider text-[#5c7368]">Retenido ($)</Label>
                          <div className="h-11 flex items-center justify-end text-xl font-bold tabular-nums text-[#205c45]" data-testid={`ret-valor-${i}`}>
                            ${(parseFloat(l.valor_retenido) || 0).toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {dialog.lineas.length > 0 && (
                    <div className="rounded-lg border border-[#b2d0c2] bg-[#e9f2ee] p-3 flex justify-between items-center">
                      <span className="font-semibold uppercase text-xs tracking-wider text-[#5c7368]">Total retenido</span>
                      <span className="text-2xl font-bold tabular-nums text-[#205c45]" data-testid="ret-total">${totalRet.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="checkbox" checked={enviarEmail} onChange={(e) => setEnviarEmail(e.target.checked)} data-testid="send-ret-email" />
                <span>Enviar comprobante por correo al proveedor (si tiene email registrado)</span>
              </label>

              {/* Feb 2026: fecha de emisión editable (por defecto = hoy) */}
              <div className="mt-3 flex items-end gap-3 bg-[#fef7ec] border border-[#f5c477] rounded p-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-[#8a5500]">Fecha de emisión retención *</Label>
                  <Input
                    type="date"
                    value={dialog.fecha || ""}
                    onChange={(e) => setDialog(d => ({ ...d, fecha: e.target.value }))}
                    className="h-10 w-44"
                    data-testid="ret-fecha-emision"
                  />
                </div>
                <div className="text-[11px] text-[#7a4000] flex-1">
                  ℹ️ Por defecto se usa la fecha de <strong>hoy</strong>. Puedes cambiarla si emites la retención por una factura de días anteriores (recuerda que SRI exige máx. 5 días hábiles desde la factura).
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={generando}>Cancelar</Button>
            <Button onClick={generar} disabled={generando} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="confirm-gen-retencion">
              {generando ? (<><Loader2 size={14} className="mr-2 animate-spin" />Procesando con el SRI…</>) : "Generar y firmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prompt automático de retención SRI tras crear la factura */}
      <Dialog open={!!retentionPrompt} onOpenChange={(o) => !o && setRetentionPrompt(null)}>
        <DialogContent className="max-w-md" data-testid="retention-prompt-modal">
          <DialogHeader><DialogTitle>¿Generar retención SRI ahora?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#4a5c53] space-y-2">
            <p>La factura <strong>{retentionPrompt?.numero_factura}</strong> de <strong>{retentionPrompt?.proveedor_nombre}</strong> por
              <span className="tabular-nums font-semibold"> ${retentionPrompt?.total?.toFixed(2)}</span> fue registrada correctamente.</p>
            <p>Si su empresa es agente de retención, puede generar el comprobante SRI ahora mismo.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetentionPrompt(null)} data-testid="retention-skip">Más tarde</Button>
            <Button className="bg-[#205c45]" onClick={() => { const fc = retentionPrompt; setRetentionPrompt(null); abrirGenerar(fc); }} data-testid="retention-now">
              Generar retención
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edición de campos seguros de la factura de compra */}
      <Dialog open={!!editFc} onOpenChange={(o) => !o && setEditFc(null)}>
        <DialogContent className="max-w-2xl" data-testid="edit-fc-dialog">
          <DialogHeader><DialogTitle>Editar factura de compra</DialogTitle></DialogHeader>
          {editFc && (
            <div className="space-y-3">
              <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-xs text-[#8a5500]">
                Sólo se permiten cambios en datos descriptivos (número, autorización, fechas, observaciones).
                Para modificar productos, cantidades o costos, elimine la factura y vuelva a crearla.
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nº Factura proveedor</Label>
                  <Input value={editFc.numero_factura} onChange={(e) => setEditFc({ ...editFc, numero_factura: e.target.value })} data-testid="edit-numero" />
                </div>
                <div>
                  <Label>Autorización SRI</Label>
                  <Input
                    value={editFc.autorizacion_sri}
                    onChange={(e) => setEditFc({ ...editFc, autorizacion_sri: e.target.value.replace(/\s/g, "").slice(0, 49) })}
                    maxLength={49}
                    placeholder="49 dígitos"
                  />
                  <div className="text-[10px] text-[#5c7368] mt-0.5 tabular-nums">{(editFc.autorizacion_sri || "").length}/49</div>
                </div>
                <div>
                  <Label>Fecha factura</Label>
                  <Input type="date" value={editFc.fecha_factura} onChange={(e) => setEditFc({ ...editFc, fecha_factura: e.target.value })} />
                </div>
                <div>
                  <Label>Días de crédito</Label>
                  <Input type="number" min="0" max="365" value={editFc.dias_credito ?? ""} onChange={(e) => setEditFc({ ...editFc, dias_credito: e.target.value === "" ? null : parseInt(e.target.value) || 0 })} />
                  <p className="text-[11px] text-[#5c7368] italic mt-1">La fecha de vencimiento se recalcula automáticamente.</p>
                </div>
                <div className="col-span-2">
                  <Label>Observaciones</Label>
                  <Input value={editFc.observaciones} onChange={(e) => setEditFc({ ...editFc, observaciones: e.target.value })} />
                </div>
              </div>
              {editFc._retencion_numero && (
                <div className="text-xs text-[#5c7368] italic">
                  Esta factura tiene retención asociada <span className="font-mono font-semibold">{editFc._retencion_numero}</span>.
                  Los cambios aquí NO actualizan los datos del XML ya firmado.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditFc(null)} disabled={busyAction}>Cancelar</Button>
            <Button onClick={guardarEdit} disabled={busyAction} className="bg-[#205c45]" data-testid="save-edit-fc-btn">
              {busyAction ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ver detalle de factura compra (read-only) */}
      <Dialog open={!!viewFc} onOpenChange={(o) => !o && setViewFc(null)}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="view-fc-dialog">
          <DialogHeader>
            <DialogTitle>Detalle factura compra · {viewFc?.numero_factura}</DialogTitle>
          </DialogHeader>
          {viewFc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-[#f5f8f6] rounded p-2">
                  <div className="text-[10px] uppercase text-[#5c7368]">Proveedor</div>
                  <div className="font-semibold">{viewFc.proveedor_nombre}</div>
                </div>
                <div className="bg-[#f5f8f6] rounded p-2">
                  <div className="text-[10px] uppercase text-[#5c7368]">Fecha emisión</div>
                  <div>{viewFc.fecha_factura}</div>
                </div>
                <div className="bg-[#f5f8f6] rounded p-2">
                  <div className="text-[10px] uppercase text-[#5c7368]">Vencimiento</div>
                  <div>{viewFc.fecha_vencimiento || "—"}</div>
                </div>
                <div className="bg-[#f5f8f6] rounded p-2">
                  <div className="text-[10px] uppercase text-[#5c7368]">Autorización SRI</div>
                  <div className="font-mono text-[10px] break-all">{viewFc.autorizacion_sri || "—"}</div>
                </div>
              </div>

              <div className="border border-[#d1dbd6] rounded overflow-hidden">
                <div className="px-3 py-2 bg-[#f7f9f8] border-b text-xs uppercase tracking-wider font-semibold text-[#5c7368]">
                  Productos ingresados ({(viewFc.lineas || []).length})
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[#fafafa] text-[11px]">
                    <tr>
                      <th className="text-left p-2">Producto</th>
                      <th className="text-left p-2">Lote</th>
                      <th className="text-left p-2">Vence</th>
                      <th className="text-right p-2">Cant.</th>
                      <th className="text-right p-2">Costo U.</th>
                      <th className="text-right p-2">Subtotal</th>
                      <th className="text-right p-2">IVA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewFc.lineas || []).map((l, i) => (
                      <tr key={i} className="border-t border-[#eee]">
                        <td className="p-2">
                          <div className="font-medium">{l.producto_nombre || "—"}</div>
                          {l.codigo_interno && <div className="text-[10px] text-[#5c7368] font-mono">{l.codigo_interno}</div>}
                        </td>
                        <td className="p-2 font-mono text-xs">{l.numero_lote || "—"}</td>
                        <td className="p-2 text-xs">{l.fecha_vencimiento || "—"}</td>
                        <td className="p-2 text-right tabular-nums">{l.cantidad}</td>
                        <td className="p-2 text-right tabular-nums">${(+l.costo_unitario || 0).toFixed(4)}</td>
                        <td className="p-2 text-right tabular-nums font-semibold">${(+l.subtotal || 0).toFixed(2)}</td>
                        <td className="p-2 text-right tabular-nums text-[#5c7368]">${(+l.iva || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {(!viewFc.lineas || viewFc.lineas.length === 0) && (
                      <tr><td colSpan={7} className="text-center py-6 text-[#8ba397]">Sin líneas registradas</td></tr>
                    )}
                  </tbody>
                  <tfoot className="bg-[#f5f8f6] text-sm font-semibold">
                    <tr>
                      <td colSpan={5} className="p-2 text-right">Subtotal:</td>
                      <td className="p-2 text-right tabular-nums" colSpan={2}>${(+viewFc.subtotal || 0).toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="p-2 text-right">IVA:</td>
                      <td className="p-2 text-right tabular-nums" colSpan={2}>${(+viewFc.iva || 0).toFixed(2)}</td>
                    </tr>
                    <tr className="border-t border-[#d1dbd6]">
                      <td colSpan={5} className="p-2 text-right text-[#205c45]">TOTAL:</td>
                      <td className="p-2 text-right tabular-nums text-[#205c45] text-lg" colSpan={2}>${(+viewFc.total || 0).toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {viewFc.observaciones && (
                <div className="bg-[#f5f8f6] rounded p-3 text-sm">
                  <div className="text-[10px] uppercase text-[#5c7368] mb-1">Observaciones</div>
                  <div className="whitespace-pre-wrap">{viewFc.observaciones}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewFc(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Confirmación de eliminación con reversa */}
      <Dialog open={!!deleteFc} onOpenChange={(o) => !o && setDeleteFc(null)}>
        <DialogContent className="max-w-lg" data-testid="delete-fc-dialog">
          <DialogHeader><DialogTitle>Eliminar factura {deleteFc?.numero_factura}</DialogTitle></DialogHeader>
          {deleteFc && (
            <div className="space-y-3 text-sm">
              <p>Esta acción revertirá los efectos contables y de inventario de la factura:</p>
              <ul className="list-disc list-inside text-[#4a5c53] space-y-1">
                <li>Elimina la <strong>retención SRI</strong> asociada (solo si NO está autorizada por el SRI).</li>
                <li>Elimina los <strong>lotes</strong> creados (solo si no se ha consumido nada de su stock).</li>
                <li>Elimina la <strong>cuenta por pagar</strong> (solo si no ha recibido pagos).</li>
                <li>Reversa el asiento contable.</li>
                <li>Queda registrado en auditoría.</li>
              </ul>
              <div className="rounded-md border border-[#b53a25]/40 bg-[#fdecea] p-3 text-[#7d2a1a] text-xs">
                <strong>No se puede deshacer.</strong> Si la retención ya fue autorizada por el SRI, o se vendieron unidades del lote, o hay pagos parciales, la operación será bloqueada con el motivo.
              </div>
              <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3 text-xs space-y-0.5">
                <div><span className="text-[#5c7368]">Proveedor:</span> <strong>{deleteFc.proveedor_nombre}</strong></div>
                <div><span className="text-[#5c7368]">Total:</span> <span className="tabular-nums font-semibold">${deleteFc.total?.toFixed(2)}</span></div>
                {deleteFc.retencion_numero && (
                  <div><span className="text-[#5c7368]">Retención asociada:</span> <span className="font-mono">{deleteFc.retencion_numero}</span></div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFc(null)} disabled={busyAction}>Cancelar</Button>
            <Button onClick={eliminarFactura} disabled={busyAction} className="bg-[#b53a25] hover:bg-[#8f2a18]" data-testid="confirm-delete-fc-btn">
              <Trash2 size={14} className="mr-2" />
              {busyAction ? "Eliminando…" : "Sí, eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== COMPARACIÓN DE COSTOS =====================
function ComparacionCostos() {
  const [bases, setBases] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState([]);
  useEffect(() => { api.get("/masters/productos-base").then(r => setBases(r.data)); }, []);
  useEffect(() => {
    if (selected) api.get(`/purchases/comparacion-costos/${selected}`).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
  }, [selected]);
  return (
    <div className="mt-4 space-y-4">
      <div className="max-w-md">
        <Label>Producto base</Label>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="compare-base-select"><SelectValue placeholder="Seleccionar producto base..." /></SelectTrigger>
          <SelectContent>{bases.map(b => <SelectItem key={b.id} value={b.id}>{b.principio_activo} {b.concentracion ? `· ${b.concentracion}${b.unidad_concentracion||""}` : ""}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {selected && (
        <>
          <div className="rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-3 text-xs text-[#205c45]">
            <strong>Costo unidad mínima</strong> = costo promedio ponderado actualizado en cada factura de compra ({"costo_existente × stock + costo_nueva × cantidad ÷ total"}).
            La columna <strong>Última factura</strong> muestra el costo real de la última compra (útil para detectar variaciones del proveedor).
          </div>
          <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
            <table className="erp-table">
              <thead><tr>
                <th>Código</th>
                <th>Producto comercial</th>
                <th className="text-right" title="Costo promedio ponderado por unidad mínima">Costo Unidad</th>
                <th className="text-right" title="Costo por presentación (caja)">Costo Caja</th>
                <th className="text-right" title="Costo de la última factura recibida">Última Factura</th>
                <th>Última compra</th>
                <th className="text-right">Stock</th>
                <th className="text-right">Precio venta</th>
              </tr></thead>
              <tbody>
                {data.map((d, i) => {
                  const variacion = d.ultimo_costo != null && d.ultimo_costo_factura != null && d.ultimo_costo > 0
                    ? ((d.ultimo_costo_factura - d.ultimo_costo) / d.ultimo_costo) * 100
                    : null;
                  return (
                    <tr key={i}>
                      <td className="font-mono text-xs">{d.codigo_interno}</td>
                      <td className="font-medium">{d.nombre_comercial}</td>
                      <td className="text-right tabular-nums font-semibold">{d.ultimo_costo != null ? `$${d.ultimo_costo.toFixed(4)}` : "—"}</td>
                      <td className="text-right tabular-nums">{d.costo_caja != null ? `$${d.costo_caja.toFixed(4)}` : "—"}</td>
                      <td className="text-right tabular-nums">
                        {d.ultimo_costo_factura != null ? (
                          <>
                            <div>${d.ultimo_costo_factura.toFixed(4)}</div>
                            {variacion != null && Math.abs(variacion) > 1 && (
                              <div className={`text-[10px] ${variacion > 0 ? "text-[#b53a25]" : "text-[#205c45]"}`}>
                                {variacion > 0 ? "▲" : "▼"} {Math.abs(variacion).toFixed(1)}%
                              </div>
                            )}
                          </>
                        ) : "—"}
                      </td>
                      <td className="text-xs text-[#5c7368]">{d.ultima_compra ? d.ultima_compra.slice(0, 10) : "—"}</td>
                      <td className="text-right tabular-nums">{d.stock_actual}</td>
                      <td className="text-right tabular-nums font-semibold">{d.precio_venta != null ? `$${d.precio_venta.toFixed(4)}` : "—"}</td>
                    </tr>
                  );
                })}
                {data.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8ba397]">Sin variantes comerciales para este producto base</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
